/*
 * Minimal `window.Webflow` so third-party scripts that defer through it actually run.
 *
 * THE BUG THIS FIXES — and it is bigger than it looks:
 *
 * Dropping webflow.js also dropped `window.Webflow`. Every script on the page that follows Webflow's
 * documented "run me once Webflow is ready" convention does:
 *
 *     window.Webflow = window.Webflow || [];
 *     window.Webflow.push(callback);
 *
 * With no webflow.js to drain it, `window.Webflow` stayed a plain array and the callbacks sat in it
 * forever. Measured on /blog/heavy-sleeper: `window.Webflow` was `{0,1,2,3}` — FOUR stranded
 * callbacks — while production had the real object with push/ready/require/env.
 *
 * What was silently broken as a result:
 *
 *   1. Finsweet Attributes never initialized AT ALL. `fsAttributes.toc`, `.richtext` and
 *      `.socialshare` all stayed PENDING (their `loading` promise never settled, and each still
 *      carried an unrun `resolve`) where production had them RESOLVED with `destroy`. Visible symptom:
 *      the table of contents on all 229 blog posts rendered its two dummy template rows
 *      ("H2 Item entry. Can wrap to multiple lines…") instead of the real headings — 2 links where
 *      production builds 26. Social sharing was dead too.
 *
 *   2. The digital-sparks cookie-consent-boost's GTM wiring never ran. Its callback is the one that
 *      waits for `"FsCC" in window` and then pushes consent state into `dataLayer`. So consent may not
 *      have propagated to GTM at all — a compliance問題, not a cosmetic one. See RET-2778.
 *
 * None of it produced a console error, and the HTML is byte-identical to production either way, so no
 * parity or visual check could have caught it. It surfaced only because a human looked at a page.
 *
 * WHAT THIS DELIBERATELY IS NOT: a reimplementation of Webflow's runtime. IX2 interactions, navbars,
 * tabs and forms are handled in src/lib/interactions.ts. This file exists purely so the `push`/`ready`
 * convention resolves. `require()` returns undefined, which is honest — we do not ship Webflow's
 * modules — and callers that feature-detect will take their fallback path.
 */

type Callback = () => void;

interface WebflowShim {
  push(fn: Callback): void;
  ready(fn: Callback): void;
  destroy(): void;
  require(name: string): undefined;
  env(mode?: string): undefined;
  redraw: { up(): void };
  resize: { up(): void; off(): void };
  scroll: { off(): void; up(): void };
  location(url: string): void;
}

declare global {
  interface Window {
    Webflow?: WebflowShim | Callback[] | unknown;
  }
}

/** Run now if the document is parsed, otherwise on DOMContentLoaded. Mirrors Webflow.ready(). */
/*
 * Real webflow.js silently tolerates `Webflow.ready(undefined)`, and @finsweet/attributes-toc calls it
 * exactly that way (`at G (…/attributes-toc@1/toc.js)`). Passing it through to runSafely() threw
 * `TypeError: fn2 is not a function` on every blog post with a table of contents — ~229 pages, at
 * 185-200ms, 4/4 trials, ours only.
 *
 * Harmless in effect (runSafely isolates it, and ToC link counts match production) but it is a permanent
 * console error that production does not have, and console noise is where the next real error hides.
 */
function whenReady(fn: Callback): void {
  if (typeof fn !== 'function') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => runSafely(fn), { once: true });
  } else {
    // A microtask, not synchronous: a callback that throws must not take the caller down with it,
    // and Webflow's own push() is asynchronous too.
    Promise.resolve().then(() => runSafely(fn));
  }
}

function runSafely(fn: Callback): void {
  try {
    fn();
  } catch (err) {
    // Webflow swallows these as well. Log rather than rethrow so one bad third-party callback cannot
    // stop the others — the failure mode this whole file exists to prevent.
    console.error('[webflow-shim] queued callback threw:', err);
  }
}

export function installWebflowShim(): void {
  const existing = window.Webflow;

  // Anything already queued before we loaded. In practice this is empty, because the engine script
  // runs before the deferred Finsweet scripts — but a stray inline push earlier in the document would
  // otherwise be lost, which is precisely the class of bug being fixed.
  const queued: Callback[] = Array.isArray(existing) ? (existing.filter((f) => typeof f === 'function') as Callback[]) : [];

  // A real Webflow is already present (someone re-added webflow.js). Leave it alone.
  if (existing && !Array.isArray(existing) && typeof (existing as WebflowShim).require === 'function') return;

  const shim: WebflowShim = {
    push: whenReady,
    ready: whenReady,
    destroy: () => {},
    require: () => undefined,
    env: () => undefined, // falsy for every mode: not the designer, not the editor, not preview.
    redraw: { up: () => {} },
    resize: { up: () => {}, off: () => {} },
    scroll: { off: () => {}, up: () => {} },
    location: (url: string) => {
      window.location.href = url;
    },
  };

  window.Webflow = shim;
  for (const fn of queued) whenReady(fn);
}
